import { Alert, Button, Tooltip } from 'antd';
import { useState } from 'react';
import type { useWorkOrderMoves } from '../hooks/use-work-order-moves';
import type { WorkOrderPermissions } from '../hooks/use-work-order-permissions';
import { offeredWorkOrderMoves } from '../policies/work-order-moves.policy';
import type { WorkOrderApprovalReadiness, WorkOrderMove, WorkOrderView } from '../work-orders.api';
import { WorkOrderMoveModal, type InputMove } from './WorkOrderMoveModal';

const LABELS: Record<WorkOrderMove, string> = {
  approve: 'Approve',
  reject: 'Reject',
  schedule: 'Schedule',
  start: 'Start work',
  complete: 'Mark complete',
  cancel: 'Cancel',
};
const FORWARD: readonly WorkOrderMove[] = ['approve', 'schedule', 'start', 'complete'];
const NEEDS_INPUT: readonly WorkOrderMove[] = ['reject', 'cancel', 'schedule'];

interface WorkOrderActionsProps {
  order: WorkOrderView;
  permissions: WorkOrderPermissions;
  readiness: WorkOrderApprovalReadiness | null;
  moves: ReturnType<typeof useWorkOrderMoves>;
}

/** Only the moves this person may make from this state, which is a convenience; the server refuses the rest. Approve is shown disabled, with why, while the quotes it needs are missing. */
export function WorkOrderActions({ order, permissions, readiness, moves }: WorkOrderActionsProps) {
  const [asking, setAsking] = useState<InputMove | null>(null);
  const offered = offeredWorkOrderMoves({
    state: order.state,
    severity: order.severity,
    raisedById: order.raisedById,
    currentUserId: permissions.userId ?? '',
    canManage: permissions.canManage,
    canApprove: permissions.canApprove,
  });
  const approveBlocked = readiness !== null && !readiness.isMet;

  function press(move: WorkOrderMove) {
    moves.clearError();
    if (NEEDS_INPUT.includes(move)) setAsking(move as InputMove);
    else void moves.perform(move);
  }

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {offered.map((move) => (
          <Tooltip key={move} title={move === 'approve' && approveBlocked ? readiness.reasons.join(' ') : undefined}>
            <span>
              <Button
                type={FORWARD.includes(move) ? 'primary' : 'default'}
                danger={move === 'reject' || move === 'cancel'}
                disabled={(move === 'approve' && approveBlocked) || moves.moving !== null}
                loading={moves.moving === move}
                onClick={() => press(move)}
              >
                {LABELS[move]}
              </Button>
            </span>
          </Tooltip>
        ))}
      </div>
      {moves.error && !asking && <Alert className="mt-3" type="error" title={moves.error} showIcon />}
      <WorkOrderMoveModal
        move={asking}
        submitting={moves.moving !== null}
        error={moves.error}
        onSubmit={(move, body) => moves.perform(move, body)}
        onClose={() => setAsking(null)}
      />
    </>
  );
}
